import { createReducer } from 'redux-create-reducer'
import type { ExplorerPane, SectionPane } from 'types/explorer'
import type { TrackId } from 'types/track'
import type { BriefId, BriefProjectId } from 'types/brief'
import { AGGREGATED_SEARCH_QUERY_SUCCESS } from '@disco/constants/ActionTypes'
import {
  EXPLORER_UPDATE,
  EXPLORER_UPLOADS_ADD,
  EXPLORER_UPLOADS_REMOVE,
  EXPLORER_CLOSE_PANE,
  GROUP_CREATE_SUCCESS,
  GROUP_DELETE_SUCCESS,
  GROUP_UPDATE_SUCCESS,
  PANE_LOAD,
  PANE_LOAD_FAIL,
  PANE_LOAD_SUCCESS,
  PANE_UPDATE,
  PANES_UPDATE,
  PANE_RECENTS_PUSH,
  PLAYBACK_PANE_UPDATE,
  BRIEF_PANE_UPDATE,
  PLAYLIST_DELETE_BATCH_SUCCESS,
  PLAYLIST_LOAD_BATCH,
  PLAYLIST_LOAD_PLAIN_UPDATE,
  PLAYLIST_VERSION_REPLACE_SUCCESS,
  SEARCH_QUERY_SUCCESS,
  TRACK_DELETE_BATCH_SUCCESS,
  TRACK_LOAD_BATCH,
  TRACK_MERGE_SUCCESS,
  TRACK_PLAY,
} from '@disco/constants/ActionTypes'
import { CUSTOM_CHANNEL } from '@disco/constants/GroupTypes'
import { ALBUM, GROUP, PLAYLIST, TRACK } from '@disco/constants/ItemTypes'
import {
  BRIEF_LIST_PANE,
  PLAYLIST_PANE,
  SEARCH_RESULTS_PANE,
  TRACK_PANE,
  SECTION_LIST_PANE,
} from '@disco/constants/PaneTypes'
import {
  ARTIST_VIEW,
  DEFAULT_VIEW,
  TRACK_VIEW,
  TRACKLESS_VIEWS,
} from '@disco/constants/ViewTypes'
import { groupUrlsContainGroupIds } from '@disco/utils/groups'
const initialState = {
  panes: [],
  activePane: 0,
  playbackPane: null,
  uploads: [],
}
export type State = {
  panes: (ExplorerPane | SectionPane)[]
  activePane: number
  playbackPane: any
  uploads: string[]
}

function getExtendedTrackGroupUrls(track) {
  if (!track.playlist_versions) return []
  const groupUrls: string[] = []
  track.playlist_versions.forEach(playlist => {
    groupUrls.push(...playlist.groups)
  })
  return groupUrls
}

function findItemPaneIndex(state, item) {
  return state.panes.findIndex(p => {
    switch (item.type) {
      case PLAYLIST:
        return p.type === PLAYLIST_PANE && p.playlist_id === item.playlist_id

      case TRACK:
        return p.type === TRACK_PANE && p.id === item.id

      case 'brief':
        return p.type === SEARCH_RESULTS_PANE && p.meta?.briefId === item.id

      case 'brief_project':
        return p.type === BRIEF_LIST_PANE && p.meta?.briefProjectId === item.id

      default:
        return false
    }
  })
}

function closePane(state, index) {
  return {
    ...state,
    panes: state.panes.filter(p => p.index < index),
    activePane: index > 0 ? index - 1 : 0,
  }
}

function getSortedPaneIndexes(state, items) {
  return items
    .map(item => {
      return {
        item: item,
        index: findItemPaneIndex(state, item),
      }
    })
    .filter(itemPane => itemPane.index > 0)
    .sort((a, b) => a.index - b.index)
}

// function checks if we need to close pane because it's no longer belongs
// to results in previous pane
function closePaneOnItemsChange(state, items) {
  // get sorted by pane index items
  const itemPaneIndexes = getSortedPaneIndexes(state, items)

  // check we need to close something
  for (let i = 0; i < itemPaneIndexes.length; i++) {
    const { item, index } = itemPaneIndexes[i]
    const previousPane = state.panes[index - 1]
    const removedFromGroup =
      previousPane.type === SEARCH_RESULTS_PANE &&
      (previousPane.groupId || previousPane.groupIds) &&
      !groupUrlsContainGroupIds(item.groups, [
        previousPane.groupId,
        ...(previousPane.groupIds || []),
      ])
    if (removedFromGroup) return closePane(state, index)
  }

  return state
}

// function checks if we need to close the pane if its item was deleted
function closePaneOnItemsDelete(state, items) {
  const itemPaneIndexes = getSortedPaneIndexes(state, items)
  if (itemPaneIndexes.length > 0)
    return closePane(state, itemPaneIndexes[0].index)
  return state
}

function updateSearchResultsPaneOnPlaylistChange(playlists, p) {
  if (
    p.type !== SEARCH_RESULTS_PANE ||
    !p.groupId ||
    !p.results ||
    [ARTIST_VIEW, TRACK_VIEW].includes(p.view)
  )
    return p
  const playlistsToRemove: any[] = []
  const playlistsToAdd: any[] = []
  playlists.forEach(playlist => {
    const playlistExists = (p.results ?? []).some(item => {
      return item.type === PLAYLIST && item.playlist_id === playlist.playlist_id
    })
    const { groups } = playlist
    const playlistInGroup = groupUrlsContainGroupIds(groups, [
      p.groupId,
      ...(p.groupIds || []),
    ])

    if (!playlistInGroup && playlistExists) {
      playlistsToRemove.push(playlist)
    } else if (playlistInGroup && !playlistExists) {
      playlistsToAdd.push(playlist)
    }
  })
  const clearedResults = (p.results ?? []).filter(item => {
    return (
      item.type !== PLAYLIST ||
      !playlistsToRemove.find(
        playlist => playlist.playlist_id === item.playlist_id,
      )
    )
  })
  const insertIndex = clearedResults.filter(
    item => item.type === PLAYLIST || item.type === GROUP,
  ).length
  // TODO: refactor activeItem splitting
  const activeItemIndex = Number(p.activeItem?.split('/')?.[1] || 0)
  let activeItemId = p.activeItem

  if (p.activeItem !== undefined && p.results) {
    activeItemId = String(p.results[activeItemIndex]?.id)
  }

  return {
    ...p,
    activeItemId,
    results: [
      ...clearedResults.slice(0, insertIndex),
      ...playlistsToAdd,
      ...clearedResults.slice(insertIndex),
    ],
  }
}

// updates tracks and track info in pane, can be dropped once we get rid of
// stashed threads, as they save whole info about panes
function updatePlaylistPaneOnPlaylistChange(playlist, p) {
  if (
    p.type !== PLAYLIST_PANE ||
    p.loading ||
    p.playlist_id !== playlist.playlist_id
  ) {
    return p
  }

  if (
    p.tracks_info &&
    playlist.tracks_info &&
    (p.tracks_info.next !== playlist.tracks_info.next ||
      (p.tracks ?? []).length !== playlist.tracks.length)
  ) {
    const tracks = playlist.tracks.map(track => {
      return track.id ? track.id : track
    })
    return { ...p, ...playlist, tracks: tracks }
  }

  return p
}

function closePaneOnGroupChange(state, group) {
  // close group pane if group was deleted
  const paneIndex = state.panes.findIndex(p => {
    return p.type === SEARCH_RESULTS_PANE && p.groupId === group.id
  })
  return paneIndex > -1 ? closePane(state, paneIndex) : state
}

function handlePlaylistChange(state, playlists) {
  // if panes are empty do nothing
  if (!state.panes) return state
  // handle only fully loaded playlists
  const loadedPlaylists = playlists.filter(playlist => playlist.groups)
  const resultState = closePaneOnItemsChange(state, playlists)
  const resultPanes = resultState.panes.map(p => {
    if (p.type === SEARCH_RESULTS_PANE) {
      return updateSearchResultsPaneOnPlaylistChange(loadedPlaylists, p)
    } else if (p.type === PLAYLIST_PANE) {
      const updatedPlaylist = loadedPlaylists.find(
        playlist => playlist.playlist_id === p.playlist_id,
      )

      if (updatedPlaylist) {
        return updatePlaylistPaneOnPlaylistChange(updatedPlaylist, p)
      }
    }

    return p
  })
  return { ...resultState, panes: resultPanes }
}

function handleAggregatedSearch(state, action) {
  const resultPanes = state.panes.map(p => {
    if (p.type !== SEARCH_RESULTS_PANE) return p
    return {
      ...p,
      aggregatedSearchResults: {
        searchResults: action.params.aggregatedResults,
        next: action.params.aggregatedNextPageUrl,
      },
    }
  })
  return { ...state, panes: resultPanes }
}

function handleTracksChange(state, tracks) {
  // if panes are empty do nothing
  if (!state.panes) return state
  // handle only fully loaded tracks
  const loadedTracks = tracks.filter(track => track.groups)
  const resultState = closePaneOnItemsChange(state, loadedTracks)
  const resultPanes = resultState.panes.map(p => {
    // we had to take care only about "search results"
    if (
      p.type !== SEARCH_RESULTS_PANE ||
      (p.groupType === CUSTOM_CHANNEL &&
        p.results &&
        p.type === SEARCH_RESULTS_PANE) ||
      !p.groupId ||
      !p.results ||
      TRACKLESS_VIEWS.includes(p.view)
    )
      return p
    // only custom channels can have tracks in default view
    if (p.groupType !== CUSTOM_CHANNEL && (!p.view || p.view === DEFAULT_VIEW))
      return p
    const tracksToRemove: any[] = []
    const tracksToAdd: any[] = []
    loadedTracks.forEach(loadedTrack => {
      const trackExists = (p.results ?? []).some(
        item => item.type === TRACK && item.id === loadedTrack.id,
      )
      const trackInGroup = groupUrlsContainGroupIds(loadedTrack.groups, [
        p.groupId,
        ...(p.groupIds || []),
      ])

      if (trackExists && !trackInGroup) {
        // special treatment for track view as it may contain tracks that
        // don't have group id set but belong to playlist that belongs to chosen
        // group
        if (p.view === TRACK_VIEW && loadedTrack.playlist_versions) {
          const groupUrls = getExtendedTrackGroupUrls(loadedTrack)

          if (
            !groupUrlsContainGroupIds(groupUrls, [
              p.groupId,
              ...(p.groupIds || []),
            ])
          ) {
            tracksToRemove.push(loadedTrack)
          }
        } else {
          tracksToRemove.push(loadedTrack)
        }
      } else if (!trackExists && trackInGroup) {
        tracksToAdd.push(loadedTrack)
      }
    })
    return {
      ...p,
      results: [
        ...(p.results ?? []).filter(item => {
          return (
            item.type !== TRACK ||
            !tracksToRemove.find(track => track.id === item.id)
          )
        }),
        ...tracksToAdd,
      ],
    }
  })
  return { ...resultState, panes: resultPanes }
}

const removeTracksFromSearchPane = (pane: ExplorerPane, trackIds) => ({
  ...pane,
  results: (pane.results ?? []).filter(item => {
    return item.type !== TRACK || trackIds.indexOf(item.id) === -1
  }),
})

function closePanesOnTracksRemoval(state, trackIds) {
  // close track pane if track was deleted
  const resultState = closePaneOnItemsDelete(
    state,
    trackIds.map(id => {
      // closePaneOnItemsDelete expects object, not simple id
      return {
        id: id,
        type: TRACK,
        groups: [],
      }
    }),
  )
  // special treatment for searchResults, they need further refactoring
  const resultPanes = resultState.panes.map(p => {
    if (p.type === SEARCH_RESULTS_PANE)
      return removeTracksFromSearchPane(p, trackIds)

    if (p.type === PLAYLIST_PANE) {
      return {
        ...p,
        tracks: (p.tracks ?? []).filter(
          trackId => trackIds.indexOf(trackId) === -1,
        ),
      }
    }

    if (p.type === SECTION_LIST_PANE)
      return {
        ...p,
        sections: (p.sections ?? []).map(section => {
          return {
            ...section,
            data: (section.data ?? []).filter(item => {
              return item.type !== TRACK || !trackIds.includes(item.id)
            }),
          }
        }),
      }
    return p
  })
  return {
    ...resultState,
    panes: resultPanes,
    playbackPane: resultState.playbackPane
      ? removeTracksFromSearchPane(resultState.playbackPane, trackIds)
      : null,
  }
}

const closePaneOnPlaylistChange = (state, playlistIds, deleted) => {
  // close playlist pane if playlist was deleted
  const resultState = closePaneOnItemsDelete(
    state,
    playlistIds.map(id => {
      // closePaneOnItemsDelete expects object, not simple id
      return {
        playlist_id: id,
        type: PLAYLIST,
      }
    }),
  )
  // if deleted remove playlists from the search results list
  const resultPanes = deleted
    ? resultState.panes.map(p => {
        if (p.type === SEARCH_RESULTS_PANE)
          return {
            ...p,
            results: (p.results ?? []).filter(item => {
              return (
                item.type !== PLAYLIST ||
                playlistIds.indexOf(item.playlist_id) === -1
              )
            }),
          }
        if (p.type === SECTION_LIST_PANE)
          return {
            ...p,
            sections: (p.sections ?? []).map(section => {
              return {
                ...section,
                data: (section.data ?? []).filter(item => {
                  return (
                    item.type !== PLAYLIST || !playlistIds.includes(item.id)
                  )
                }),
              }
            }),
          }
        return p
      })
    : resultState.panes
  return { ...resultState, panes: resultPanes }
}

function closePanesOnBriefRemoval(state, briefId: BriefId) {
  const resultState = closePaneOnItemsDelete(
    state,
    [briefId].map(id => {
      // closePaneOnItemsDelete expects object, not simple id
      return {
        id: id,
        type: 'brief',
      }
    }),
  )

  const resultPanes = resultState.panes.map(p => {
    if (p.type === BRIEF_LIST_PANE) {
      return {
        ...p,
        results: (p.results ?? []).filter(item => {
          return (
            item.type !== 'brief' ||
            (item.type === 'brief' && item.id !== briefId)
          )
        }),
      }
    }
    return p
  })
  return { ...state, panes: resultPanes }
}

function closePanesOnBriefProjectRemoval(
  state,
  briefProjectId: BriefProjectId,
) {
  const resultState = closePaneOnItemsDelete(
    state,
    [briefProjectId].map(id => {
      return {
        id: id,
        type: 'brief_project',
      }
    }),
  )

  const resultPanes = resultState.panes.map(p => {
    if (p.type === BRIEF_LIST_PANE) {
      return {
        ...p,
        results: (p.results ?? []).filter(item => {
          return item.type !== 'brief_project' || item.id !== briefProjectId
        }),
      }
    }
    return p
  })
  return { ...resultState, panes: resultPanes }
}

/**
 * Value of the `X-Disco-GroupIDs` request header for a pane, or '' when the
 * pane isn't channel scoped.
 *
 * Every channel pane shares the same URL (`/api/browse/`) and identifies its
 * channel(s) through this header instead, so the header value is also the only
 * way to tell which channel a pane's response belongs to.
 * `isStaleGroupResponse` depends on both sides deriving from this one builder;
 * a separate reimplementation could drift and silently disable the guard.
 */
export const getPaneGroupHeader = (pane: Partial<ExplorerPane>): string => {
  if (pane.groupIds) return JSON.stringify(pane.groupIds)
  if (pane.groupId) return JSON.stringify([pane.groupId])
  return ''
}

/**
 * True when the response belongs to a different channel than the one now
 * occupying that pane slot - i.e. the user switched channels while this
 * request was in flight, so applying it would restore the channel they left.
 */
function isStaleGroupResponse(
  state: State,
  pane: Partial<ExplorerPane>,
): boolean {
  const existingPane =
    pane.index === undefined ? undefined : state.panes[pane.index]
  if (!existingPane || existingPane.type !== pane.type) return false
  const existingGroupKey = getPaneGroupHeader(existingPane)
  const incomingGroupKey = getPaneGroupHeader(pane)
  // an empty header means "not channel scoped", which is not comparable
  return (
    existingGroupKey !== '' &&
    incomingGroupKey !== '' &&
    existingGroupKey !== incomingGroupKey
  )
}

function updatePaneOnBriefProjectCreation(state, briefProject) {
  const panes = state.panes.map(p => {
    if (p.index > 0 || p.type !== BRIEF_LIST_PANE || !p.results) {
      return p
    }

    return {
      ...p,
      results: [
        {
          ...briefProject,
          type: 'brief_project',
        },
        ...p.results,
      ],
    }
  })

  return { ...state, panes }
}

export const initReducer: (
  prefix?: string,
) => (state: State, action: any) => State = (prefix: string = '') =>
  createReducer<State, any>(initialState, {
    [`${prefix}${PANE_LOAD}`](state, action) {
      const { pane, view } = action
      return {
        ...state,
        panes: [
          ...state.panes.slice(0, pane.index),
          { ...pane, loading: true, loaded: false, view },
        ],
        activePane: pane.index,
      }
    },

    [`${prefix}${PANE_LOAD_SUCCESS}`](state, action) {
      const { pane, result } = action

      // prevent overriding existing pane with results from previous
      // long running request
      if (isStaleGroupResponse(state, pane)) return state

      if (pane.index < state.panes.length) {
        const existingPane = state.panes[pane.index] as ExplorerPane
        const isSearchResultsPane = existingPane.type === SEARCH_RESULTS_PANE
        const groupIsTheSame = existingPane.groupId === pane.groupId
        const isBriefsPane = existingPane.type === BRIEF_LIST_PANE

        const viewSwitch =
          existingPane.type === pane.type &&
          ((isSearchResultsPane && groupIsTheSame) || isBriefsPane)

        // For brief inbox panes: prevent race conditions when a stale API response
        if (
          pane.groupType === 'brief_inbox' &&
          result &&
          existingPane.meta?.briefId !== undefined &&
          pane.meta?.briefId !== undefined &&
          existingPane.meta?.briefId !== pane.meta?.briefId
        ) {
          return state
        } else if (pane.groupType !== 'brief_inbox') {
          // For non-brief panes: use the existing URL check
          // skip check for search query results (url is undefined)
          // skip check for view switch
          // compare panes of the same type only
          if (
            !viewSwitch &&
            pane.url &&
            existingPane.type === pane.type &&
            existingPane.url !== pane.url
          ) {
            return state
          }
        }
      }

      let newPane = {
        ...pane,
        ...result,
        loading: false,
        loaded: true,
        error: null,
      }

      // If this pane has a activateByPlaylistId in meta, find and highlight that playlist
      if (pane.meta?.activateByPlaylistId && result.results) {
        const playlistIndex = result.results.findIndex(
          (item: any) =>
            item.type === 'playlist' &&
            item.playlist_id === pane.meta.activateByPlaylistId,
        )

        if (playlistIndex !== -1) {
          const itemPrefix = pane.view || 'pane'
          // Remove activateByPlaylistId from meta after using it
          const { activateByPlaylistId, ...remainingMeta } = pane.meta || {}
          newPane = {
            ...newPane,
            activeItem: `${itemPrefix}/${playlistIndex}`,
            meta: remainingMeta,
          }
        }
      }

      return { ...state, panes: [...state.panes.slice(0, pane.index), newPane] }
    },

    [`${prefix}${PANE_LOAD_FAIL}`](state, action) {
      const { pane, error } = action

      if (isStaleGroupResponse(state, pane)) return state

      const newPane = { ...pane, loading: false, loaded: true, error: error }
      return {
        ...state,
        panes: [
          ...state.panes.slice(0, pane.index),
          newPane,
          ...state.panes.slice(pane.index + 1),
        ],
      }
    },

    [`${prefix}${PANE_UPDATE}`](state, action) {
      const { result } = action

      // a pagination page from a channel the user already switched away from
      // must not merge into the pane that replaced it; in-channel updates
      // (renames, sorts, activeItem) carry the same group and pass through
      if (isStaleGroupResponse(state, result)) return state

      const newPane = { ...state.panes[result.index], ...result }
      return {
        ...state,
        panes: [
          ...state.panes.slice(0, result.index),
          newPane,
          ...state.panes.slice(result.index + 1),
        ],
      }
    },

    [`${prefix}${PANES_UPDATE}`](state, action) {
      let panes = state.panes
      action.results.forEach(result => {
        const newPane = { ...state.panes[result.index], ...result }
        panes = [
          ...panes.slice(0, result.index),
          newPane,
          ...panes.slice(result.index + 1),
        ]
      })
      return { ...state, panes }
    },

    [`${prefix}${PANE_RECENTS_PUSH}`](state, action) {
      const { item, itemType } = action
      const recentsPane = state.panes?.find(
        pane => pane.type === SECTION_LIST_PANE,
      ) as SectionPane

      if (!recentsPane?.sections) {
        return state
      }

      const sectionTitleMap = {
        [TRACK]: 'Tracks',
        [PLAYLIST]: 'Playlists',
        [GROUP]: 'Channels',
        [ALBUM]: 'Albums',
      }

      const sectionTitle = sectionTitleMap[itemType]

      if (!sectionTitle) {
        return state
      }

      const itemId = itemType === PLAYLIST ? item.playlist_id : item.id
      const section = recentsPane.sections.find(
        section => section.title === sectionTitle,
      )

      if (!section) {
        return state
      }

      const existingItemIndex = section.data.findIndex(
        (dataItem: any) => dataItem.type === itemType && dataItem.id === itemId,
      )

      const newItem = {
        type: itemType,
        id: itemId,
      }

      let updatedData
      if (existingItemIndex >= 0) {
        updatedData = [
          newItem,
          ...section.data.filter((_, index) => index !== existingItemIndex),
        ]
      } else {
        updatedData = [newItem, ...section.data]
      }

      if (updatedData.length > 3) {
        updatedData = updatedData.slice(0, 3)
      }

      const updatedSections = recentsPane.sections.map(sectionItem =>
        sectionItem.title === sectionTitle
          ? { ...sectionItem, data: updatedData }
          : sectionItem,
      )

      const recentsIndex = state.panes.findIndex(
        pane => pane.type === SECTION_LIST_PANE,
      )

      const updatedPanes = [
        ...state.panes.slice(0, recentsIndex),
        {
          ...recentsPane,
          sections: updatedSections,
        },
        ...state.panes.slice(recentsIndex + 1),
      ]

      return { ...state, panes: updatedPanes }
    },

    [`${prefix}${EXPLORER_UPDATE}`](state, action) {
      const { result } = action
      return { ...state, ...result }
    },

    [PLAYLIST_DELETE_BATCH_SUCCESS](state, { playlistIds }) {
      return closePaneOnPlaylistChange(state, playlistIds, true)
    },

    [PLAYLIST_VERSION_REPLACE_SUCCESS](state, { replacedPlaylists }) {
      return closePaneOnPlaylistChange(
        state,
        replacedPlaylists.map(p => p.playlist_id),
        false,
      )
    },

    [TRACK_DELETE_BATCH_SUCCESS](
      state,
      {
        trackIds,
      }: {
        trackIds: TrackId[]
      },
    ) {
      return closePanesOnTracksRemoval(state, trackIds)
    },

    [TRACK_MERGE_SUCCESS](state, { tracksToMerge }) {
      const trackIds = tracksToMerge.map(t => t.id)
      return closePanesOnTracksRemoval(state, trackIds)
    },

    [GROUP_CREATE_SUCCESS](state, action) {
      // special treatment for searchResults, they need further refactoring
      const panes: ExplorerPane[] = (state.panes as ExplorerPane[]).map(p => {
        if (
          p.type !== SEARCH_RESULTS_PANE ||
          p.groupId !== action.group.parent
        ) {
          return p
        }

        const insertIndex = (p.results ?? []).filter(
          item => item.type === GROUP,
        ).length
        return {
          ...p,
          results: [
            ...(p.results ?? []).slice(0, insertIndex),
            action.group,
            ...(p.results ?? []).slice(insertIndex),
          ],
        }
      })
      return { ...state, panes: panes }
    },

    [GROUP_DELETE_SUCCESS](state, action) {
      const resultState = closePaneOnGroupChange(state, action.group)
      // special treatment for searchResults, they need further refactoring
      const resultPanes = resultState.panes.map(p => {
        if (p.type !== SEARCH_RESULTS_PANE) return p
        return {
          ...p,
          results: (p.results ?? []).filter(item => {
            return item.type !== GROUP || item.id !== action.group.id
          }),
        }
      })
      return { ...resultState, panes: resultPanes }
    },

    [GROUP_UPDATE_SUCCESS](state, action) {
      const resultState = action.parentChanged
        ? closePaneOnGroupChange(state, action.group)
        : state
      const resultPanes: ExplorerPane[] = resultState.panes.map(p => {
        if (p.type !== SEARCH_RESULTS_PANE) return p

        // pane name is a snapshot from open time; refresh so renames show without reopening
        if (p.groupId === action.group.id) {
          return { ...p, name: action.group.name }
        }

        if (p.groupId !== action.group.parent) {
          // remove group if it will be found in results
          return {
            ...p,
            results: (p.results ?? []).filter(item => {
              return item.type !== GROUP || item.id !== action.group.id
            }),
          }
        } else {
          // if it already exist in results, do nothing
          const groupExists = (p.results ?? []).find(item => {
            return item.type !== GROUP || item.id === action.group.id
          })
          if (groupExists) return p
          const insertIndex = (p.results ?? []).filter(
            item => item.type === GROUP,
          ).length
          return {
            ...p,
            results: [
              ...(p.results ?? []).slice(0, insertIndex),
              action.group,
              ...(p.results ?? []).slice(insertIndex),
            ],
          }
        }
      })
      return { ...resultState, panes: resultPanes }
    },

    [PLAYLIST_LOAD_PLAIN_UPDATE](state, { playlists }) {
      return handlePlaylistChange(state, playlists)
    },

    [PLAYLIST_LOAD_BATCH](state, action) {
      // handle only single playlist updates, if multiple playlists loaded
      // this means it's not save operation, revisit when we have multiple
      // item operations
      if (action.list && action.list.length > 0) {
        return handlePlaylistChange(state, action.list)
      }

      return state
    },

    [TRACK_LOAD_BATCH](state, action) {
      if (action.list && action.list.length > 0) {
        return handleTracksChange(state, action.list)
      }

      return state
    },

    [SEARCH_QUERY_SUCCESS](state) {
      return { ...state, activePane: 0 }
    },

    [AGGREGATED_SEARCH_QUERY_SUCCESS](state, action) {
      return handleAggregatedSearch(state, action)
    },

    // TODO: playback pane doesn't really belong to explorer reducer and should be
    // moved to media reducer, especially with the fact that in mobile app
    // several explorer reducers exists and user must be able to play tracks
    // from any of them, as a workaround give a way to pass the playback pane
    // as param, after merge with the web app, move playbackPane to media reducer
    [`${prefix}${TRACK_PLAY}`](state, { playbackPane }) {
      return playbackPane?.type === SEARCH_RESULTS_PANE
        ? { ...state, playbackPane }
        : { ...state, playbackPane: null }
    },

    [`${prefix}${PLAYBACK_PANE_UPDATE}`](state, { pane }) {
      return { ...state, playbackPane: pane }
    },

    [`${prefix}${BRIEF_PANE_UPDATE}`](state, { brief }) {
      const updatedPanes = state.panes.map(pane => {
        if (pane.type !== BRIEF_LIST_PANE || !pane.results) return pane

        const hasUpdatedBrief = pane.results.some(
          (item: any) => item.type === 'brief' && item.id === brief.id,
        )

        if (hasUpdatedBrief) {
          const updatedResults = pane.results.map((item: any) => {
            if (item.type === 'brief' && item.id === brief.id) {
              return {
                ...item,
                submission_status: brief.submission_status,
                deadline: brief.deadline,
                deadline_timezone: brief.deadline_timezone,
              }
            }
            return item
          })

          return {
            ...pane,
            results: updatedResults,
          }
        }

        return pane
      })

      return { ...state, panes: updatedPanes }
    },

    [EXPLORER_UPLOADS_ADD](state, { ids }) {
      return { ...state, uploads: [...state.uploads, ...ids] }
    },

    [EXPLORER_UPLOADS_REMOVE](state, { ids }) {
      return {
        ...state,
        uploads: state.uploads.filter(id => !ids.includes(id)),
      }
    },

    [`${prefix}${EXPLORER_CLOSE_PANE}`](state, { index }) {
      return closePane(state, index)
    },

    ['briefs/deleteBrief/fulfilled'](state, action) {
      const briefId = action.meta.arg
      return closePanesOnBriefRemoval(state, briefId)
    },
    ['briefProjects/deleteBriefProject/fulfilled'](state, action) {
      const briefProjectId = action.meta.arg
      return closePanesOnBriefProjectRemoval(state, briefProjectId)
    },
    ['briefProjects/createBriefProject/fulfilled'](state, action) {
      const briefProject = action.payload
      return updatePaneOnBriefProjectCreation(state, briefProject)
    },
  })
export const explorerReducer = initReducer()
